import { useEffect } from "react";

/**
 * Snaps the window back to the top whenever iOS leaves the native scroll view
 * overscrolled on a document that never scrolls.
 *
 * The app shell locks `html`/`body` to the viewport (`height: 100%;
 * overflow: hidden`) and scrolls inside inner containers, so the window's
 * own scroll offset should always be 0. Two iOS behaviours break that:
 *
 * - Chrome for iOS shrinks its web view around the address bar / bottom
 *   toolbar and compensates every frame change by nudging the native scroll
 *   offset. On a non-scrollable document that nudge is an overscroll, which
 *   WebKit does not clamp until the user pans the page.
 * - WebKit scrolls the window to reveal a focused input above the keyboard
 *   and does not always undo it when the keyboard closes.
 *
 * In both cases WebKit moves the layout viewport along with the overscroll:
 * `position: fixed` chrome (the bottom navigation) floats above the bottom
 * edge, the sticky header disappears under the browser bar, and the `html`
 * background shows through underneath. Resetting the window scroll offset
 * clamps the native scroll view and restores the layout.
 */

const IOS_UA = /iPhone|iPad|iPod/i;
const RECHECK_DELAYS_MS = [0, 250, 1000, 2500];
const KEYBOARD_DISMISS_DELAYS_MS = [50, 350, 750];

export function isIosLikeDevice(nav: Navigator = navigator): boolean {
  if (IOS_UA.test(nav.userAgent)) return true;
  // iPadOS reports itself as a Mac but exposes touch points.
  return nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1;
}

function isEditableElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return (el as HTMLElement).isContentEditable === true;
}

/**
 * Returns the window scroll offset the document can legitimately hold. Any
 * offset beyond it is an overscroll left behind by the browser.
 */
export function bogusWindowScrollOffset(win: Window = window): number | null {
  const doc = win.document.documentElement;
  const maxScrollTop = Math.max(0, doc.scrollHeight - win.innerHeight);
  const scrollY = win.scrollY;
  if (!Number.isFinite(scrollY) || scrollY <= maxScrollTop) return null;
  return maxScrollTop;
}

export default function useViewportOffsetReset(): void {
  useEffect(() => {
    if (typeof window === "undefined" || !isIosLikeDevice()) return;

    const timers = new Set<number>();
    let frame = 0;

    const reset = () => {
      frame = 0;
      // Pinch-zoom legitimately offsets the visual viewport; leave it alone.
      const scale = window.visualViewport?.scale ?? 1;
      if (scale !== 1) return;
      // While the keyboard is open WebKit needs the offset to keep the field
      // visible; the focusout handler re-checks once the keyboard is gone.
      if (isEditableElement(document.activeElement)) return;
      const target = bogusWindowScrollOffset();
      if (target === null) return;
      window.scrollTo(0, target);
    };

    const scheduleReset = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(reset);
    };

    const scheduleDelayed = (delays: number[]) => {
      for (const delay of delays) {
        const id = window.setTimeout(() => {
          timers.delete(id);
          scheduleReset();
        }, delay);
        timers.add(id);
      }
    };

    const onFocusOut = () => scheduleDelayed(KEYBOARD_DISMISS_DELAYS_MS);
    const onPageShow = () => scheduleDelayed(RECHECK_DELAYS_MS);

    // A document-level scroll on a non-scrollable document is the overscroll
    // itself; inner containers' scroll events do not bubble to the window.
    window.addEventListener("scroll", scheduleReset, { passive: true });
    window.addEventListener("resize", scheduleReset);
    window.addEventListener("orientationchange", scheduleReset);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("load", onPageShow);
    document.addEventListener("focusout", onFocusOut);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", scheduleReset);
    vv?.addEventListener("scroll", scheduleReset);

    // The browser toolbar insets settle shortly after first paint.
    scheduleDelayed(RECHECK_DELAYS_MS);

    return () => {
      window.removeEventListener("scroll", scheduleReset);
      window.removeEventListener("resize", scheduleReset);
      window.removeEventListener("orientationchange", scheduleReset);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("load", onPageShow);
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", scheduleReset);
      vv?.removeEventListener("scroll", scheduleReset);
      if (frame) window.cancelAnimationFrame(frame);
      for (const id of timers) window.clearTimeout(id);
    };
  }, []);
}
